import {
  BaseChartDataItem,
  FinancesGrossBreakdown,
  FinancesVGrubsProviderRow,
  GroupByType,
  lightTheme,
} from '@vgrubs/components';
import { BreakdownItem, FinanceTabbedCard } from './FinanceTabbedCard';
import {
  SERVICE_COLORS,
  SERVICE_FEE_LABEL,
  SERVICE_FEE_TOOLTIP,
  VDRIVE_NEGATIVE_FEE_LABEL,
  VDRIVE_NEGATIVE_FEE_TOOLTIP,
} from './serviceLegend';
import { buildVGrubsProviderRows } from './vGrubsProviderRows';

// Same services, same colors and same order as the Net Revenue pie, so the
// two cards can be read side by side.
const GROSS_LABELS: {
  key: keyof FinancesGrossBreakdown;
  label: string;
  tooltip?: string;
}[] = [
  { key: 'vGrubs', label: 'vGrubs VR' },
  { key: 'vOrders', label: 'vOrders' },
  { key: 'saas', label: 'Monthly Saas' },
  { key: 'vDrive', label: 'vDrive' },
  {
    key: 'vDriveNegativeFee',
    label: VDRIVE_NEGATIVE_FEE_LABEL,
    tooltip: VDRIVE_NEGATIVE_FEE_TOOLTIP,
  },
  { key: 'serviceFee', label: SERVICE_FEE_LABEL, tooltip: SERVICE_FEE_TOOLTIP },
  { key: 'withdraw', label: 'vPay Fee' },
];

/** Gross — Pie/Graph tabbed card. */
export const GrossCard = ({
  grossSeries,
  grossSeriesCompared,
  grossSum,
  grossSumToCompare,
  groupByType,
  isComparing,
  grossBreakdown,
  vGrubsProviderBreakdown,
  periodLabel,
  onOpenSaasDetails,
  onOpenServiceFeeDetails,
}: {
  grossSeries: BaseChartDataItem[];
  grossSeriesCompared: BaseChartDataItem[];
  grossSum: number;
  grossSumToCompare: number;
  groupByType: GroupByType;
  isComparing: boolean;
  grossBreakdown: FinancesGrossBreakdown | null | undefined;
  /** vGrubs VR split per order provider - opens from that legend row. */
  vGrubsProviderBreakdown?: FinancesVGrubsProviderRow[] | null;
  /** Active date range, shown under the headline. */
  periodLabel?: string;
  /** Opens what Monthly Saas is made of - from that legend row's arrow. */
  onOpenSaasDetails?: () => void;
  /** Opens where the Service Fee came from - from that legend row's arrow. */
  onOpenServiceFeeDetails?: () => void;
}) => {
  // Order volume per provider. No order count here: the count the API sends
  // is the one behind `net`, and gross and net cover different orders.
  const vGrubsProviderRows = buildVGrubsProviderRows(
    vGrubsProviderBreakdown,
    'gross',
    false
  );

  const breakdownItems: BreakdownItem[] = GROSS_LABELS.map(
    ({ key, label, tooltip }) => ({
      label,
      tooltip,
      amount: grossBreakdown?.[key] ?? 0,
      color: SERVICE_COLORS[key],
      drilldown:
        key === 'vGrubs' && vGrubsProviderRows.length
          ? { title: 'Gross by Provider', rows: vGrubsProviderRows }
          : undefined,
      onOpen:
        key === 'saas'
          ? onOpenSaasDetails
          : key === 'serviceFee'
          ? onOpenServiceFeeDetails
          : undefined,
    })
  );

  return (
    <FinanceTabbedCard
      title="Gross Revenue"
      headlineCents={grossSum}
      headlineCompareCents={grossSumToCompare}
      valueColor={lightTheme.green}
      series={grossSeries}
      seriesCompared={grossSeriesCompared}
      seriesKey="gross"
      tooltipLabel="Gross"
      breakdownItems={breakdownItems}
      breakdownAmountLabel="Gross"
      breakdownLegendLimit={GROSS_LABELS.length}
      periodLabel={periodLabel}
      groupByType={groupByType}
      isComparing={isComparing}
    />
  );
};
